/**
 * Feedback List Component
 * Displays a list of feedback items in the dashboard
 */

import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { FeedbackWithStatus, FeedbackType } from '../../../types/feedback';

interface FeedbackListProps {
  feedback: FeedbackWithStatus[];
  onSelect: (feedback: FeedbackWithStatus) => void;
  isLoading: boolean;
}

const FeedbackList: React.FC<FeedbackListProps> = ({
  feedback,
  onSelect,
  isLoading
}) => {
  const [selectedScreenshot, setSelectedScreenshot] = useState<string | null>(null);
  const getFeedbackTypeIcon = (type: FeedbackType) => {
    const icons = {
      'quick-reaction': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
        </svg>
      ),
      'page-comment': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      ),
      'bug-report': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="8" y="6" width="8" height="14" rx="4"/>
          <path d="M6 6h4"/>
          <path d="M6 10h4"/>
          <path d="M6 14h4"/>
          <path d="M14 6h4"/>
          <path d="M14 10h4"/>
          <path d="M14 14h4"/>
        </svg>
      ),
      'feature-request': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={3}/>
          <path d="M12 1v6m0 6v6"/>
          <path d="M21 12h-6m-6 0H3"/>
        </svg>
      ),
      'element-feedback': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 9l-4 4v3a2 2 0 0 0 2 2h3l4-4"/>
          <path d="M14 5l4 4-6.5 6.5a2 2 0 0 1-3-3L14 5z"/>
        </svg>
      ),
      'flow-rating': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      ),
      'general': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
        </svg>
      )
    };
    return icons[type];
  };

  const getTypeLabel = (type: FeedbackType): string => {
    const labels: Record<FeedbackType, string> = {
      'quick-reaction': 'Quick Reaction',
      'page-comment': 'Page Comment',
      'element-feedback': 'Element Feedback',
      'flow-rating': 'Flow Rating',
      'bug-report': 'Bug Report',
      'feature-request': 'Feature Request',
      'general': 'General'
    };
    return labels[type];
  };

  const getTypeColor = (type: FeedbackType): string => {
    const colors: Record<FeedbackType, string> = {
      'quick-reaction': 'text-sage-600 bg-sage-50',
      'page-comment': 'text-steel-600 bg-steel-50',
      'element-feedback': 'text-amber-600 bg-amber-50',
      'flow-rating': 'text-purple-600 bg-purple-50',
      'bug-report': 'text-coral-600 bg-coral-50',
      'feature-request': 'text-indigo-600 bg-indigo-50',
      'general': 'text-stone-600 bg-stone-50'
    };
    return colors[type];
  };

  const getPriorityColor = (priority: string): string => {
    const colors = {
      low: 'bg-sage-100 text-sage-700',
      medium: 'bg-amber-100 text-amber-700',
      high: 'bg-coral-100 text-coral-700',
      urgent: 'bg-red-100 text-red-700'
    };
    return colors[priority as keyof typeof colors] || colors.medium;
  };

  const truncateText = (text: string, maxLength: number = 100): string => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  const getFeedbackTitle = (feedback: FeedbackWithStatus): string | null => {
    switch (feedback.type) {
      case 'bug-report':
        return (feedback as any).title;
      case 'feature-request':
        return (feedback as any).title;
      case 'general':
        return (feedback as any).subject;
      default:
        return null;
    }
  };

  const getFeedbackPreview = (feedback: FeedbackWithStatus): string => {
    switch (feedback.type) {
      case 'quick-reaction':
        const quickData = feedback as any;
        const reactionText = `Reaction: ${quickData.reaction}`;
        return quickData.comment ? `${reactionText} - ${quickData.comment}` : reactionText;
      
      case 'page-comment':
        return (feedback as any).comment || 'No comment provided';
      
      case 'bug-report':
        // Only show description since title is shown separately
        const bugData = feedback as any;
        return bugData.description || 'No description provided';
      
      case 'feature-request':
        // Only show description since title is shown separately
        const featureData = feedback as any;
        return featureData.description || 'No description provided';
      
      case 'general':
        // Only show message since subject is shown separately
        const generalData = feedback as any;
        return generalData.message || 'No message provided';
      
      case 'element-feedback':
        const elementData = feedback as any;
        const elementText = elementData.feedbackText || 'No feedback text provided';
        const suggestion = elementData.suggestion ? ` - Suggestion: ${elementData.suggestion}` : '';
        return `${elementText}${suggestion}`;
      
      case 'flow-rating':
        const flowData = feedback as any;
        const rating = `Rating: ${flowData.rating}/5`;
        const status = ` - Status: ${flowData.completionStatus}`;
        const suggestions = flowData.suggestions ? ` - ${flowData.suggestions}` : '';
        return `${rating}${status}${suggestions}`;
      
      default:
        return 'No preview available';
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-stone-200 rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="h-4 bg-stone-200 rounded w-24"></div>
                    <div className="h-4 bg-stone-200 rounded w-16"></div>
                    <div className="h-4 bg-stone-200 rounded w-20"></div>
                  </div>
                  <div className="h-3 bg-stone-200 rounded w-full"></div>
                  <div className="h-3 bg-stone-200 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (feedback.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 text-stone-300">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <h3 className="text-lg font-medium text-slate-800 mb-2">No feedback found</h3>
        <p className="text-slate-600">
          No feedback matches your current filters. Try adjusting the filters or check back later.
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-stone-200">
      {feedback.map((item) => (
        <button
          key={item.id}
          onClick={() => onSelect(item)}
          className="w-full p-6 text-left hover:bg-stone-50 transition-colors focus:outline-none 
                     focus:ring-2 focus:ring-inset focus:ring-terracotta"
        >
          <div className="flex items-start gap-4">
            {/* Type Icon */}
            <div className={cn(
              'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
              getTypeColor(item.type)
            )}>
              {getFeedbackTypeIcon(item.type)}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-slate-800">
                  {getTypeLabel(item.type)}
                </span>
                
                <span className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                  getPriorityColor(item.priority)
                )}>
                  {item.priority}
                </span>

                <span className="text-xs text-slate-500">
                  {formatDate(item.timestamp)}
                </span>
              </div>

              {/* Title/Subject (if available) */}
              {getFeedbackTitle(item) && (
                <h4 className="text-sm font-semibold text-slate-800 mb-1 leading-tight">
                  {truncateText(getFeedbackTitle(item)!, 80)}
                </h4>
              )}

              {/* Description/Content */}
              <p className="text-sm text-slate-600 mb-2 leading-relaxed">
                {truncateText(getFeedbackPreview(item))}
              </p>

              {/* Screenshot Thumbnail */}
              {((item as any).thumbnailBase64 || (item as any).screenshotUrl) && (
                <div className="mb-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedScreenshot((item as any).thumbnailBase64 || (item as any).screenshotUrl);
                    }}
                    className="relative group"
                    title="Click to view full screenshot"
                  >
                    <img
                      src={(item as any).thumbnailBase64 ? 
                        `data:image/png;base64,${(item as any).thumbnailBase64}` : 
                        (item as any).screenshotUrl
                      }
                      alt="Screenshot thumbnail"
                      className="w-12 h-12 object-cover rounded-lg border border-stone-200 hover:border-terracotta group-hover:shadow-md transition-all"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg flex items-center justify-center transition-all">
                      <svg className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 3h6v6M10 14l11-11M21 3l-7 7M3 21v-6h6"/>
                      </svg>
                    </div>
                  </button>
                </div>
              )}

              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r={10}/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                  {new URL(item.pageUrl).pathname}
                </span>
                
                {item.viewport && (
                  <span className="flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                      <line x1="8" y1="21" x2="16" y2="21"/>
                      <line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                    {item.viewport.width}×{item.viewport.height}
                  </span>
                )}

                {item.upvotes && item.upvotes > 0 && (
                  <span className="flex items-center gap-1 text-terracotta-600">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                    </svg>
                    {item.upvotes}
                  </span>
                )}
              </div>
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0 text-stone-400">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </div>
          </div>
        </button>
      ))}
      
      {/* Screenshot Viewer Modal */}
      {selectedScreenshot && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4"
          onClick={() => setSelectedScreenshot(null)}
        >
          <div className="relative bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <button
              onClick={() => setSelectedScreenshot(null)}
              className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-lg hover:bg-opacity-75 transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
            <img
              src={selectedScreenshot.startsWith('data:') ? selectedScreenshot : `data:image/png;base64,${selectedScreenshot}`}
              alt="Full screenshot"
              className="max-w-full max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackList;